import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Spinner } from '@1trading/react';
import { useState } from 'react';
import { ReportFilesTable } from '@/features/reports/report-files-table';
import { useReports } from '@/hooks/queries/reports/use-reports';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage } from '@/lib/api/error';
import { REPORT_TYPE_OPTIONS, type ReportsQuery, type ReportType } from '@/services/api/reports';
import { daysAgo, toDateInputValue } from '@/utils/date';

const DEFAULT_RANGE_DAYS = 7;
const FETCH_ERROR_TOAST_ID = 'reports-fetch-failed';

const defaultQuery = (): ReportsQuery => ({
  type: 'solidus',
  from: toDateInputValue(daysAgo(DEFAULT_RANGE_DAYS)),
  to: toDateInputValue(new Date()),
});

const ReportsPage = () => {
  const [draft, setDraft] = useState<ReportsQuery>(defaultQuery);
  // Auto-fetches once on mount with the defaults; afterwards only the Search
  // button moves `submitted`, so edits to the controls never fetch on their own.
  const [submitted, setSubmitted] = useState<ReportsQuery>(draft);

  const reportsQuery = useReports(submitted);
  const { data, isPending, isFetching, isError, error, refetch } = reportsQuery;

  useQueryErrorToast(reportsQuery, 'Failed to fetch reports', FETCH_ERROR_TOAST_ID);

  const handleSearch = () => {
    if (draft.type === submitted.type && draft.from === submitted.from && draft.to === submitted.to) {
      refetch();
      return;
    }
    setSubmitted(draft);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reports</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-type">Report Type</Label>
            <Select onValueChange={(value) => setDraft((previous) => ({ ...previous, type: value as ReportType }))} value={draft.type}>
              <SelectTrigger className="w-60" id="report-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-from">From</Label>
            <Input className="w-44" id="report-from" onChange={(event) => setDraft((previous) => ({ ...previous, from: event.target.value }))} type="date" value={draft.from} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-to">To</Label>
            <Input className="w-44" id="report-to" onChange={(event) => setDraft((previous) => ({ ...previous, to: event.target.value }))} type="date" value={draft.to} />
          </div>
          <Button className="gap-2" disabled={isFetching} onClick={handleSearch} type="button">
            {isFetching ? <Spinner className="size-4" /> : null}
            Search
          </Button>
        </div>
        {isError ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-red-500">Failed to fetch reports: {getErrorMessage(error)}</p>
            <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
              Retry
            </Button>
          </div>
        ) : null}
        <ReportFilesTable
          emptyMessage="No reports found. Select a report type and date range, then click Search."
          files={data?.files}
          isPending={isPending}
          viewPathname="/compliance/reports/view"
        />
      </CardContent>
    </Card>
  );
};
ReportsPage.displayName = 'ReportsPage';

export default ReportsPage;
